import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { z } from 'zod'

import ConfirmUserCreationDialog from './ConfirmUserCreationDialog'

import styles from '../styles.module.scss'

import { isApiError } from '@/api'
import { createUser } from '@/api/user'
import Button from '@/components/Button'
import FormField from '@/components/FormField/FormField'
import { useToast } from '@/components/Toast/ToastContext'

const userSchema = z.object({
  email: z.string().email(),
  first_name: z.string().min(1, 'First name cannot be empty'),
  last_name: z.string().min(1, 'Last name cannot be empty'),
})

type UserFormValues = z.infer<typeof userSchema>

const CreateUserTab = () => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [pendingFormData, setPendingFormData] = useState<UserFormValues | null>(null)
  const { updateToast } = useToast()

  const {
    control,
    handleSubmit: formHandleSubmit,
    reset,
    formState: { errors },
  } = useForm<UserFormValues>({
    resolver: zodResolver(userSchema),
  })

  const mutation = useMutation({
    mutationFn: (data: UserFormValues) => createUser(data),
    onSuccess: (result) => {
      updateToast(`User created successfully: ${result.data.email}`, 'success')
      reset()
    },
    onError: (err) => {
      console.error(err)
      const message = isApiError(err) ? err.message : 'Failed to create user'
      updateToast(message, 'error')
    },
  })

  const handleSubmit = () => {
    if (pendingFormData) {
      setShowConfirmDialog(false)
      mutation.mutate(pendingFormData)
    }
  }

  const handleCancelCreate = () => {
    setShowConfirmDialog(false)
    setPendingFormData(null)
  }

  const handleCreateUser = formHandleSubmit((data) => {
    setPendingFormData(data as UserFormValues)
    setShowConfirmDialog(true)
  })

  return (
    <>
      <ConfirmUserCreationDialog
        isOpen={showConfirmDialog}
        formData={pendingFormData}
        isLoading={mutation.isPending}
        onConfirm={handleSubmit}
        onCancel={handleCancelCreate}
      />

      <form className={styles.formSection} onSubmit={(e) => e.preventDefault()}>
        <div className={styles.formFields}>
          <h2 className={styles.sectionTitle}>Create New User</h2>
          <p className={styles.infoText}>
            Creating a new user below will send the new user an email to reset their password.
          </p>
          <FormField
            name="email"
            label="Email"
            type="email"
            control={control}
            error={errors.email}
            placeholder="user@example.com"
          />

          <FormField
            name="first_name"
            label="First Name"
            control={control}
            error={errors.first_name}
            placeholder="John"
          />

          <FormField name="last_name" label="Last Name" control={control} error={errors.last_name} placeholder="Doe" />

          <Button
            title="Create User"
            onClick={handleCreateUser}
            type="dark"
            isLoading={mutation.isPending}
            isDisabled={mutation.isPending}
          />
        </div>
      </form>
    </>
  )
}

export default CreateUserTab
